import React, { useEffect, useState, useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "../utils/apk";

function AdminHome() {
  const [stats, setStats] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState("All");
  const [premiumCustomers, setPremiumCustomers] = useState([]);

  const months = [
    "All",
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  useEffect(() => {
    const fetchOrderStats = async () => {
      try {
        const res = await axios.get(API + "/track/order/details");
        if (res.data.success) {
          setStats(res.data);
        }
      } catch (error) {
        console.error("Error fetching order stats:", error);
      }
    };

    fetchOrderStats();
  }, []);

  useEffect(() => {
    const fetchPremiumCustomers = async () => {
      try {
        const query =
          selectedMonth !== "All"
            ? `?month=${selectedMonth.toLowerCase()}`
            : "";
        const res = await axios.get(API + `/get/premimum/customer${query}`);
        if (res.data.success) {
          setPremiumCustomers(res.data.data);
        }
      } catch (error) {
        console.error("Error fetching premium customers:", error);
      }
    };

    fetchPremiumCustomers();
  }, [selectedMonth]);

  const getLastFiveDates = useMemo(() => {
    const dates = [];
    for (let i = 4; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      dates.push(d.toISOString().split("T")[0]);
    }
    return dates;
  }, []);

  const chartData = useMemo(() => {
    if (!stats || !stats.last5Days) return [];

    return [
      {
        title: "Total Orders",
        color: "#60a5fa",
        data: stats.last5Days.map((d) => ({
          date: d.date,
          value: d.totalOrders,
        })),
      },
      {
        title: "Delivered Orders",
        color: "#4ade80",
        data: stats.last5Days.map((d) => ({
          date: d.date,
          value: d.delivered,
        })),
      },
      {
        title: "Cancelled Orders",
        color: "#f87171",
        data: stats.last5Days.map((d) => ({
          date: d.date,
          value: d.cancelled,
        })),
      },
      {
        title: "Pending Orders",
        color: "#facc15",
        data: stats.last5Days.map((d) => ({
          date: d.date,
          value: d.pending,
        })),
      },
    ];
  }, [stats]);

  return (
    <div className="p-6 space-y-8 bg-blue-300 min-h-screen">
      {/* Stat Cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          <div className="bg-yellow-600 text-white rounded-md p-4 text-center shadow-md">
            <h2 className="text-lg font-semibold">Grand Total</h2>
            <p className="text-red-700 text-2xl font-bold mt-2">
              ₹{stats.grandTotalDeliveredAmount}
            </p>
          </div>
          <div className="bg-yellow-600 text-white rounded-md p-4 text-center shadow-md">
            <h2 className="text-lg font-semibold">Total Order</h2>
            <p className="text-red-700 text-2xl font-bold mt-2">
              {stats.totalOrder}
            </p>
          </div>
          <div className="bg-yellow-600 text-white rounded-md p-4 text-center shadow-md">
            <h2 className="text-lg font-semibold">Pending Order</h2>
            <p className="text-red-700 text-2xl font-bold mt-2">
              {stats.totalPending}
            </p>
          </div>
          <div className="bg-yellow-600 text-white rounded-md p-4 text-center shadow-md">
            <h2 className="text-lg font-semibold">Delivered Order</h2>
            <p className="text-red-700 text-2xl font-bold mt-2">
              {stats.totalDelivered}
            </p>
          </div>
        </div>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {chartData.map((chart, index) => (
          <motion.div
            key={index}
            className="bg-white p-6 rounded-md shadow-md h-[350px]"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: index * 0.2 }}
          >
            <h2 className="text-xl font-bold mb-4">{chart.title}</h2>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chart.data}>
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#fff",
                    borderRadius: "8px",
                    border: "1px solid #ddd",
                  }}
                  labelStyle={{ color: "#000", fontWeight: "bold" }}
                  formatter={(value) => [`${value}`, "Orders"]}
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke={chart.color}
                  strokeWidth={3}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                  animationDuration={1000}
                  animationEasing="ease-out"
                />
              </LineChart>
            </ResponsiveContainer>
          </motion.div>
        ))}
      </div>

      {/* Premium Customers Table */}
      <div className="bg-white p-6 rounded-md shadow-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Premium Customers</h2>
          <div className="flex items-center">
            <label htmlFor="month" className="mr-2 font-semibold">
              Filter by Month:
            </label>
            <select
              id="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="border border-gray-300 rounded px-3 py-1"
            >
              {months.map((month) => (
                <option key={month} value={month}>
                  {month}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full border border-gray-300 rounded-md">
            <thead className="bg-gray-100 text-gray-700">
              <tr>
                <th className="py-2 px-4 border">Customer Name</th>
                <th className="py-2 px-4 border">Address</th>
                <th className="py-2 px-4 border">Mobile No</th>
                <th className="py-2 px-4 border">Total MRP</th>
                <th className="py-2 px-4 border">Month</th>
              </tr>
            </thead>
            <tbody className="text-center">
              {premiumCustomers.length === 0 ? (
                <tr>
                  <td colSpan="5" className="py-4 text-gray-500 bg-yellow-50">
                    No customers found for selected month.
                  </td>
                </tr>
              ) : (
                premiumCustomers
                  .slice()
                  .sort((a, b) => b.totalMRP - a.totalMRP)
                  .map((item, index) => {
                    const isTopFive = index < 5;
                    return (
                      <tr
                        key={index}
                        className={
                          index % 2 === 0 ? "bg-yellow-100" : "bg-white"
                        }
                      >
                        <td className="py-2 px-4 border">
                          {isTopFive && (
                            <span className="text-yellow-500 font-bold mr-1">
                              ⭐
                            </span>
                          )}
                          {item.name}
                        </td>
                        <td className="py-2 px-4 border">{item.address}</td>
                        <td className="py-2 px-4 border">{item.mobile}</td>
                        <td className="py-2 px-4 border">{item.totalMRP}</td>
                        <td className="py-2 px-4 border">{item.month}</td>
                      </tr>
                    );
                  })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default AdminHome;
